import { useMemo } from 'react';
import messages from './messages';
import { columns } from './constants';

const highlightHeader = {
  sx: {
    backgroundColor: 'primary.dark',
  },
};
const useHeaders = ({
  intl,
  filteredColumns,
  allowances,
  isRDFloorPlan,
  isAffordableProperty,
  currentRentForecastId,
  isApproved,
}) => {
  return useMemo(() => {
    if (!currentRentForecastId) return [];

    const currentValueLabel = isApproved
      ? messages.approvedValue
      : messages.currentValue;

    return [
      {
        id: 'floorPlan',
        label: intl.formatMessage(messages.floorPlans),
        sortable: true,
      },
      {
        id: 'bedsBaths',
        label: intl.formatMessage(messages.bedsBaths),
        sortable: true,
      },

      ...(isRDFloorPlan
        ? [
            {
              id: 'unitType',
              label: intl.formatMessage(messages.unitType),
              sortable: true,
            },
          ]
        : []),
      {
        id: 'numUnits',
        label: intl.formatMessage(messages.numUnits),
        sortable: true,
      },
      ...filteredColumns.reduce((acc, columnKey) => {
        const columnValue =
          columnKey === 'baseMarketRentAmount' && isAffordableProperty
            ? 'Gross Rent Limit'
            : columns[columnKey];
        const currentColumnKey = `current${columnKey}`;
        const totalColumnKey = `total${columnKey}Income`;
        const newProposedChangeColumnKey = `new${columnKey}ProposedChange`;
        const newColumnKey = `new${columnKey}`;
        const newPotentialIncomeColumnKey = `new${columnKey}PotentialIncome`;

        return acc.concat([
          {
            id: currentColumnKey,
            label: intl.formatMessage(currentValueLabel, {
              column: columnValue,
            }),
            sortable: true,
            group: columnValue,
            dependencies: [],
          },
          {
            id: totalColumnKey,
            label: intl.formatMessage(messages.totalIncome, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.currentIncomeTooltip),
            group: columnValue,
            dependencies: [currentColumnKey],
          },
          ...(columnKey === 'noteRent'
            ? [
                {
                  id: 'actualRentSpread',
                  label: intl.formatMessage(messages.actualRentSpread),
                  sortable: true,
                  info: intl.formatMessage(messages.actualRentSpreadTooltip),
                  group: columnValue,
                  dependencies: [currentColumnKey],
                },
              ]
            : []),
          {
            id: newProposedChangeColumnKey,
            label: intl.formatMessage(
              columnKey === 'noteRent'
                ? messages.proposedRentSpread
                : messages.proposedChange,
            ),
            sortable: false,
            variant: 'div',
            group: columnValue,
            dependencies: [currentColumnKey],
            ...highlightHeader,
          },
          {
            id: newColumnKey,
            label: intl.formatMessage(messages.newValue, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.newProposedAmountTooltip),
            group: columnValue,
            dependencies: [newProposedChangeColumnKey, currentColumnKey],
            ...highlightHeader,
          },
          {
            id: newPotentialIncomeColumnKey,
            label: intl.formatMessage(messages.newPotentialIncome, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.newPotentialIncomeTooltip),
            group: columnValue,
            dependencies: [
              newColumnKey,
              newProposedChangeColumnKey,
              currentColumnKey,
            ],
            ...highlightHeader,
          },
        ]);
      }, []),
      ...allowances.reduce((acc, allowance) => {
        const columnValue = allowance.uaMessage;
        const currentColumnKey = `current${columnValue}`;
        const totalColumnKey = `total${columnValue}Income`;
        const newProposedChangeColumnKey = `new${columnValue}ProposedChange`;
        const newColumnKey = `new${columnValue}`;
        const newPotentialIncomeColumnKey = `new${columnValue}PotentialIncome`;

        return acc.concat([
          {
            id: currentColumnKey,
            label: intl.formatMessage(currentValueLabel, {
              column: columnValue,
            }),
            sortable: true,
            group: columnValue,
            dependencies: [],
          },
          {
            id: totalColumnKey,
            label: intl.formatMessage(messages.totalIncome, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.currentIncomeTooltip),
            group: columnValue,
            dependencies: [currentColumnKey],
          },
          {
            id: newProposedChangeColumnKey,
            label: intl.formatMessage(messages.proposedChange),
            sortable: false,
            variant: 'div',
            group: columnValue,
            dependencies: [],
            ...highlightHeader,
          },
          {
            id: newColumnKey,
            label: intl.formatMessage(messages.newValue, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.newProposedAmountTooltip),
            group: columnValue,
            dependencies: [newProposedChangeColumnKey],
            ...highlightHeader,
          },
          {
            id: newPotentialIncomeColumnKey,
            label: intl.formatMessage(messages.newPotentialIncome, {
              column: columnValue,
            }),
            sortable: true,
            info: intl.formatMessage(messages.newPotentialIncomeTooltip),
            group: columnValue,
            dependencies: [newColumnKey, newProposedChangeColumnKey],
            ...highlightHeader,
          },
        ]);
      }, []),
    ];
  }, [intl, filteredColumns, allowances, isApproved]);
};

export default useHeaders;
